/* PARTNER INVITES — generate a link, send it to a concrete supplier, watch them connect. */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PreviewLinkWarning } from "@/components/PreviewLinkWarning";
import { toast } from "sonner";
import { Link2, Copy, Plus, Ban, CheckCircle2, Mail, MessageSquare } from "lucide-react";

export default function PartnerInvites() {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/partner/invites").then((r) => setRows(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load invites"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/partner/invites", { label });
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Invite link created and copied");
      setLabel(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the link"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/partner/invites/${id}`); toast.success("Link revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  const copy = async (url) => { await navigator.clipboard?.writeText(url); toast.success("Copied"); };
  const share = (url) =>
    `Hi — we use ArmorBatch to see live concrete loads and ETAs for the jobs our gear is on. Connect with us here (free): ${url}`;

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-4xl" data-testid="partner-invites-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">PARTNER NETWORK</div>
        <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">Invite <span className="text-[#38BDF8]">Suppliers</span></h1>
        <p className="text-sm text-[#A1A6B0] mt-2 max-w-2xl">
          Send a link to any concrete plant you work with. They connect in under a minute — free — and you get live
          truck positions, batched times and ETAs for every job your equipment is on. One link can be used by as
          many suppliers as you like.
        </p>
      </div>

      <PreviewLinkWarning what="invite link" />

      <div className="border border-[#22262E] bg-[#11141A] p-4 flex gap-2 flex-wrap items-end">
        <label className="flex-1 min-w-[220px]">
          <div className="text-[10px] tracking-[0.2em] text-[#8B909A] uppercase mb-1">Label (optional)</div>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Tassie plants"
            data-testid="partner-invite-label" className="w-full bg-black/40 border border-[#2C313A] focus:border-[#38BDF8] outline-none text-white px-3 py-2.5 text-sm" />
        </label>
        <button type="button" onClick={create} disabled={busy} data-testid="partner-invite-create"
          className="mono uppercase tracking-widest font-black bg-[#38BDF8] text-black px-6 py-3 hover:bg-sky-300 disabled:opacity-50 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New invite link
        </button>
      </div>

      <div className="space-y-3" data-testid="partner-invite-list">
        {rows.length === 0 && <div className="border border-dashed border-[#2C313A] p-8 text-center text-sm text-[#A1A6B0]" data-testid="partner-invite-empty">No links yet — make one above.</div>}
        {rows.map((i) => (
          <div key={i.id} className={`border p-4 ${i.revoked || i.expired ? "border-[#22262E] opacity-50" : "border-[#2C313A] bg-[#11141A]"}`} data-testid={`partner-invite-row-${i.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Link2 className="w-4 h-4 text-[#38BDF8]" />
              <span className="mono text-[12px] text-white break-all">{i.url}</span>
              {i.label && <span className="mono text-[10px] uppercase tracking-widest text-[#8B909A]">· {i.label}</span>}
              {(i.revoked || i.expired) && <span className="mono text-[10px] uppercase tracking-widest text-red-400">{i.revoked ? "revoked" : "expired"}</span>}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button type="button" onClick={() => copy(i.url)} data-testid={`partner-invite-copy-${i.id}`}
                className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-2 text-[#A1A6B0] hover:border-[#38BDF8] hover:text-[#38BDF8] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy link</button>
              <a href={`sms:?&body=${encodeURIComponent(share(i.url))}`} data-testid={`partner-invite-sms-${i.id}`}
                className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-2 text-[#A1A6B0] hover:border-[#38BDF8] hover:text-[#38BDF8] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Text it</a>
              <a href={`mailto:?subject=${encodeURIComponent("Connect with us on ArmorBatch")}&body=${encodeURIComponent(share(i.url))}`} data-testid={`partner-invite-email-${i.id}`}
                className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-2 text-[#A1A6B0] hover:border-[#38BDF8] hover:text-[#38BDF8] inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Email it</a>
              {!i.revoked && (
                <button type="button" onClick={() => revoke(i.id)} data-testid={`partner-invite-revoke-${i.id}`}
                  className="ml-auto mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-2 text-[#A1A6B0] hover:border-red-500 hover:text-red-400 inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Revoke</button>
              )}
            </div>
            {(i.uses || []).length > 0 && (
              <div className="mt-3 border-t border-[#22262E] pt-2 space-y-1" data-testid={`partner-invite-uses-${i.id}`}>
                {i.uses.map((u, n) => (
                  <div key={n} className="mono text-[11px] text-emerald-400 inline-flex items-center gap-1.5 mr-4">
                    <CheckCircle2 className="w-3 h-3" /> {u.org_name} · {new Date(u.at).toLocaleDateString()}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
